<template>
  <SpinnerOverlay />
  <ConfirmDialog />
  
  <div class="app-container">
    <!-- Stunning Header -->
    <nav
      v-if="showAdminHeader"
      class="admin-navbar navbar navbar-expand-lg fixed-top"
      :class="{ 'scrolled': isScrolled }"
    >
      <div class="container-fluid">
        <!-- Left Section: Logo & Toggle -->
        <div class="navbar-brand-section">
          <button
            @click="toggleSidebar"
            class="sidebar-toggle-btn"
            type="button"
            :title="isSidebarToggled ? 'Expand Menu' : 'Collapse Menu'"
          >
            <i class="bi" :class="isSidebarToggled ? 'bi-chevron-right' : 'bi-chevron-left'"></i>
          </button>
          
          <router-link to="/" class="brand-logo" href="#">
            <div class="logo-container">
              <img
                src="/favicon.ico"
                alt="Tabulation System"
                class="logo-img"
              />
              <span class="logo-text">Tabulation System</span>
            </div>
          </router-link>
        </div>

        <!-- Center Section: Breadcrumb Navigation -->
        <div class="navigation-section">
          <nav aria-label="breadcrumb" class="custom-breadcrumb">
            <ol class="breadcrumb">
              <li 
                v-for="(crumb, index) in breadcrumbs" 
                :key="index"
                class="breadcrumb-item"
                :class="{ 'active': index === breadcrumbs.length - 1 }"
              >
                <router-link 
                  v-if="index < breadcrumbs.length - 1"
                  :to="crumb.path" 
                  class="breadcrumb-link"
                >
                  {{ crumb.name }}
                </router-link>
                <span v-else class="breadcrumb-current">
                  {{ crumb.name }}
                </span>
              </li>
            </ol>
          </nav>
        </div>

        <!-- Right Section: User Menu -->
        <div class="user-section">
          <div class="dropdown user-dropdown">
            <a 
              class="user-profile-toggle" 
              href="#" 
              id="userDropdown" 
              data-bs-toggle="dropdown" 
              aria-expanded="false"
            >
              <div class="user-avatar">
                <i class="bi bi-person-circle"></i>
                <div class="user-status-indicator"></div>
              </div>
              <div class="user-info">
                <span class="user-name">{{ currentUser ? currentUser.unique_name.toUpperCase() : "Guest" }}</span>
                <span class="user-role">{{ getUserRoleBadge() }}</span>
              </div>
              <i class="bi bi-chevron-down dropdown-arrow"></i>
            </a>
            <ul class="dropdown-menu dropdown-menu-end user-dropdown-menu" aria-labelledby="userDropdown">
              <li class="dropdown-header">
                <div class="user-profile-summary">
                  <i class="bi bi-person-badge"></i>
                  <div>
                    <strong>{{ currentUser ? currentUser.winaccountname : "" }}</strong>
                    <small class="text-muted">{{ getUserRoleDisplay() }}</small>
                  </div>
                </div>
              </li>
              <li class="dropdown-divider"></li>
              <li v-if="currentUser && hasRequiredRole(['admin', 'web-scan-live'])">
                <a href="#" @click.prevent="toggleLiveScan('live')" class="dropdown-item menu-item">
                  <i class="bi bi-qr-code-scan menu-icon"></i>
                  <span class="menu-text">Live Scan</span>
                  <span class="menu-badge new">New</span>
                </a>
              </li>
              <li v-if="currentUser && hasRequiredRole(['admin', 'web-scan-print'])">
                <a href="#" @click.prevent="toggleLiveScan('print')" class="dropdown-item menu-item">
                  <i class="bi bi-printer menu-icon"></i>
                  <span class="menu-text">ID Print Scan</span>
                </a>
              </li>
              <li class="dropdown-divider"></li>
              <li>
                <a href="#" @click.prevent="logout" class="dropdown-item menu-item logout-item">
                  <i class="bi bi-box-arrow-left menu-icon"></i>
                  <span class="menu-text">Logout</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <!-- Navigation Progress Bar -->
      <div class="navigation-progress" :class="{ 'active': isNavigating }"></div>
    </nav>

    <!-- Main Content Area -->
    <div class="main-content-container" id="content-container">
      <!-- Elegant Sidebar -->
      <div 
        v-if="showAdminHeader"
        :class="[
          'admin-sidebar',
          { 'toggled': isSidebarToggled },
        ]"
        id="sidebar-wrapper"
      >
        <div class="sidebar-header">
          <h3 class="sidebar-title">Navigation</h3>
          <div class="sidebar-subtitle">Pageant Management</div>
        </div>
        
        <div class="sidebar-menu">
          <!-- Dashboard -->
          <router-link
            to="/"
            class="menu-item"
            :class="{ 'active': $route.path === '/' }"
          >
            <div class="menu-icon-wrapper">
              <i class="bi bi-speedometer2"></i>
            </div>
            <span class="menu-text">Dashboard</span>
            <div class="active-indicator"></div>
          </router-link>

          <!-- Pageant Accordion -->
          <div class="menu-accordion">
            <div class="accordion-header" @click="togglePageantMenu">
              <div class="menu-icon-wrapper">
                <i class="bi bi-trophy"></i>
              </div>
              <span class="menu-text">Pageant</span>
              <i class="bi" :class="isPageantMenuOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
            </div>
            <div class="accordion-content" :class="{ 'open': isPageantMenuOpen }">
              <router-link 
                to="/candidates" 
                class="submenu-item"
                :class="{ 'active': $route.path === '/candidates' }"
                v-if="currentUser && hasRequiredRole(['admin', 'candidates'])"
              >
                <i class="bi bi-people-fill"></i>
                <span>Candidates</span>
                <div class="submenu-indicator"></div>
              </router-link>
              
              <router-link
                to="/events"
                class="submenu-item"
                :class="{ 'active': $route.path === '/events' }"
                v-if="currentUser && hasRequiredRole(['admin', 'events'])"
              >
                <i class="bi bi-calendar-event"></i>
                <span>Events</span>
                <div class="submenu-indicator"></div>
              </router-link>
              
              <router-link
                to="/judges"
                class="submenu-item"
                :class="{ 'active': $route.path === '/judges' }"
                v-if="currentUser && hasRequiredRole(['admin', 'judges'])"
              >
                <i class="bi bi-person-badge"></i>
                <span>Judges</span>
                <div class="submenu-indicator"></div>
              </router-link>
            </div>
          </div>

          <!-- Printing -->
          <router-link
            v-if="currentUser && hasRequiredRole(adminPrintRoles)"
            to="/print-id"
            class="menu-item"
            :class="{ 'active': $route.path === '/print-id' }"
          >
            <div class="menu-icon-wrapper">
              <i class="bi bi-printer"></i>
            </div>
            <span class="menu-text">Printing</span>
            <div class="active-indicator"></div>
          </router-link>

          <!-- Events -->
          <router-link
            to="/voter-logs"
            class="menu-item"
            :class="{ 'active': $route.path === '/voter-logs' }"
          >
            <div class="menu-icon-wrapper">
              <i class="bi bi-calendar-check"></i>
            </div>
            <span class="menu-text">Events</span>
            <div class="active-indicator"></div>
          </router-link>
        </div>
        
        <div class="sidebar-footer">
          <div class="system-status">
            <div class="status-indicator online"></div>
            <span>System Online</span>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <div id="page-content-wrapper" :class="{ 'sidebar-toggled': isSidebarToggled }">
        <div class="page-content">
          <slot/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import SpinnerOverlay from "./animations/SpinnerOverlay.vue";
import { getUserRoles, getUserObject } from "@/utils/auth";
import router from "@/router";
import ConfirmDialog from "./animations/ConfirmDialog.vue";
import store from "@/store";
import confirmationService from "@/services/confirmationService";
import { roles } from "@/utils/config";

export default {
  components: {
    SpinnerOverlay,
    ConfirmDialog,
  },
  setup() {
    const route = useRoute();
    const isSidebarToggled = ref(false);
    const isPageantMenuOpen = ref(true);
    const isScrolled = ref(false);
    const isNavigating = ref(false);
    const adminPrintRoles = computed(() => roles.adminPrint);
    const showLiveScan = ref(false);
    const scanMode = ref("live");
    const currentUser = computed(() => getUserObject());
    const showAdminHeader = ref(store.state.showAdminHeader);

    // Breadcrumbs computation
    const breadcrumbs = computed(() => {
      const crumbs = [];
      const pathArray = route.path.split('/').filter(path => path);
      
      let currentPath = '';
      // eslint-disable-next-line
      pathArray.forEach((path, index) => {
        currentPath += `/${path}`;

        crumbs.push({
          name: formatBreadcrumbName(path),
          path: currentPath
        });
      });
      
      // Add home if not present
      if (crumbs.length === 0 || crumbs[0].name !== 'Dashboard') {
        crumbs.unshift({ name: 'Dashboard', path: '/' });
      }
      
      return crumbs;
    });

    const formatBreadcrumbName = (path) => {
      const nameMap = {
        '': 'Dashboard',
        'candidates': 'Candidates',
        'events': 'Events',
        'judges': 'Judges',
        'print-id': 'Printing',
        'voter-logs': 'Event Logs'
      };
      return nameMap[path] || path.charAt(0).toUpperCase() + path.slice(1);
    };

    const getUserRoleBadge = () => {
      const userRoles = getUserRoles();
      if (userRoles.includes('admin')) return 'Administrator';
      if (userRoles.includes('judge')) return 'Judge';
      return 'User';
    };

    const getUserRoleDisplay = () => {
      const userRoles = getUserRoles();
      return userRoles.join(', ').toUpperCase();
    };

    const onScannerClosed = () => {
      showLiveScan.value = false;
    };

    const toggleLiveScan = (mode) => {
      scanMode.value = mode;
      showLiveScan.value = !showLiveScan.value;
    };

    const hasRequiredRole = (requiredRoles) => {
      return requiredRoles.some((role) => getUserRoles().includes(role));
    };

    const toggleSidebar = () => {
      isSidebarToggled.value = !isSidebarToggled.value;
    };

    const togglePageantMenu = () => {
      isPageantMenuOpen.value = !isPageantMenuOpen.value;
    };

    const logout = async () => {
      const confirmed = await confirmationService.show(
        "Are you sure you want to logout?"
      );
      if (!confirmed) return;
      store.dispatch("showAdminHeader", false);
      await store.dispatch("auth/logout");
      router.push("/login");
    };

    // Scroll handler for navbar effect
    const handleScroll = () => {
      isScrolled.value = window.scrollY > 10;
    };

    // Navigation progress handler
    const handleNavigationStart = () => {
      isNavigating.value = true;
    };

    const handleNavigationEnd = () => {
      isNavigating.value = false;
    };

    watch(
      () => store.state.showAdminHeader,
      () => {
        showAdminHeader.value = store.state.showAdminHeader;
      }
    );

    const updateHeaderVisibility = () => {
      const currentRoute = router.currentRoute.value;
      showAdminHeader.value = currentRoute.meta.showHeader !== false;
    };

    onMounted(() => {
      updateHeaderVisibility();
      window.addEventListener('scroll', handleScroll);
      router.beforeEach(handleNavigationStart);
      router.afterEach(handleNavigationEnd);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', handleScroll);
    });

    return {
      isSidebarToggled,
      isPageantMenuOpen,
      isScrolled,
      isNavigating,
      toggleSidebar,
      togglePageantMenu,
      showAdminHeader,
      currentUser,
      breadcrumbs,
      logout,
      hasRequiredRole,
      adminPrintRoles,
      showLiveScan,
      toggleLiveScan,
      onScannerClosed,
      scanMode,
      getUserRoleBadge,
      getUserRoleDisplay
    };
  },
};
</script>

<style scoped>
/* CSS Variables for Theme */
:root {
  --primary-red: #dc2626;
  --light-red: #fef2f2;
  --dark-red: #991b1b;
  --accent-red: #ef4444;
  --gradient-red: linear-gradient(135deg, #dc2626, #ef4444);
  --dark-bg: #1f2937;
  --darker-bg: #111827;
  --text-light: #f8fafc;
  --text-dark: #1f2937;
  --border-light: #e5e7eb;
  --sidebar-width: 280px;
  --sidebar-collapsed: 80px;
}

/* Main App Container */
.app-container {
  min-height: 100vh;
  background: var(--light-red);
}

/* Stunning Navbar */
.admin-navbar {
  background: linear-gradient(135deg, var(--dark-red), var(--primary-red));
  backdrop-filter: blur(10px);
  border-bottom: 3px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 20px rgba(220, 38, 38, 0.3);
  transition: all 0.3s ease;
  padding: 0.5rem 1rem;
  height: 70px;
}

.admin-navbar.scrolled {
  background: rgba(220, 38, 38, 0.95);
  backdrop-filter: blur(20px);
}

/* Navbar Sections */
.navbar-brand-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sidebar-toggle-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: white;
  padding: 0.5rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.sidebar-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.brand-logo {
  text-decoration: none;
  color: white;
}

.logo-container {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-img {
  width: 35px;
  height: 35px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.logo-text {
  font-weight: 700;
  font-size: 1.25rem;
  background: linear-gradient(45deg, #fff, #fecaca);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Navigation Section */
.navigation-section {
  flex: 1;
  display: flex;
  justify-content: center;
  margin: 0 2rem;
}

.custom-breadcrumb {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  padding: 0.5rem 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.breadcrumb {
  margin: 0;
  background: transparent;
}

.breadcrumb-item {
  color: rgba(255, 255, 255, 0.8);
}

.breadcrumb-item.active {
  color: white;
  font-weight: 600;
}

.breadcrumb-link {
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: color 0.3s ease;
}

.breadcrumb-link:hover {
  color: white;
}

.breadcrumb-current {
  color: white;
  font-weight: 600;
}

/* User Section */
.user-section {
  display: flex;
  align-items: center;
}

.user-dropdown {
  position: relative;
}

.user-profile-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 1rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  text-decoration: none;
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.user-profile-toggle:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: translateY(-2px);
}

.user-avatar {
  position: relative;
  font-size: 1.5rem;
}

.user-status-indicator {
  position: absolute;
  bottom: 2px;
  right: 2px;
  width: 8px;
  height: 8px;
  background: #10b981;
  border: 2px solid var(--primary-red);
  border-radius: 50%;
}

.user-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.user-name {
  font-weight: 600;
  font-size: 0.9rem;
}

.user-role {
  font-size: 0.75rem;
  opacity: 0.8;
  background: rgba(255, 255, 255, 0.2);
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
}

.dropdown-arrow {
  font-size: 0.8rem;
  transition: transform 0.3s ease;
}

.user-dropdown.show .dropdown-arrow {
  transform: rotate(180deg);
}

/* Dropdown Menu */
.user-dropdown-menu {
  border: none;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(20px);
  background: rgba(44, 43, 43, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.3);
  overflow: hidden;
}

.user-profile-summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: var(--text-dark);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
}

.menu-item:hover {
  background: var(--light-red);
  color: var(--primary-red);
  transform: translateX(5px);
}

.menu-icon {
  width: 20px;
  text-align: center;
}

.menu-badge.new {
  background: var(--primary-red);
  color: rgb(29, 28, 28);
  padding: 0.1rem 0.5rem;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 600;
}

.logout-item {
  color: var(--primary-red);
  font-weight: 600;
}

/* Navigation Progress Bar */
.navigation-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0%;
  height: 3px;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  transition: width 0.3s ease;
}

.navigation-progress.active {
  width: 100%;
  animation: progressAnimation 1.5s ease-in-out infinite;
}

@keyframes progressAnimation {
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Main Content Layout */
.main-content-container {
  margin-top: 70px;
  min-height: calc(100vh - 70px);
  display: flex;
}

/* Stunning Sidebar */
.admin-sidebar {
  width: var(--sidebar-width);
  background: linear-gradient(180deg, var(--darker-bg), var(--dark-bg));
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);
}

.admin-sidebar.toggled {
  transform: translateX(-100%);
}

.sidebar-header {
  padding: 2rem 1.5rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.sidebar-title {
  color: white;
  font-weight: 700;
  font-size: 1.25rem;
  margin: 0;
}

.sidebar-subtitle {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  margin-top: 0.25rem;
}

/* Sidebar Menu */
.sidebar-menu {
  flex: 1;
  padding: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  border-left: 3px solid transparent;
}

.menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border-left-color: var(--primary-red);
}

.menu-item.active {
  background: rgba(220, 38, 38, 0.2);
  color: white;
  border-left-color: var(--primary-red);
}

.menu-icon-wrapper {
  width: 20px;
  text-align: center;
  font-size: 1.1rem;
}

.menu-text {
  flex: 1;
  font-weight: 500;
}

.active-indicator {
  width: 6px;
  height: 6px;
  background: var(--primary-red);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.menu-item.active .active-indicator {
  opacity: 1;
}

/* Accordion Styles */
.menu-accordion {
  border-radius: 0;
}

.accordion-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  cursor: pointer;
  transition: all 0.3s ease;
  border-left: 3px solid transparent;
}

.accordion-header:hover {
  background: rgba(255, 255, 255, 0.1);
  color: white;
}

.accordion-content {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
}

.accordion-content.open {
  max-height: 300px;
}

.submenu-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1.5rem 0.75rem 3rem;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  position: relative;
}

.submenu-item:hover {
  background: rgba(255, 255, 255, 0.05);
  color: white;
}

.submenu-item.active {
  color: white;
  background: rgba(220, 38, 38, 0.15);
}

.submenu-indicator {
  width: 4px;
  height: 4px;
  background: var(--primary-red);
  border-radius: 50%;
  opacity: 0;
}

.submenu-item.active .submenu-indicator {
  opacity: 1;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.system-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
}

.status-indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.status-indicator.online {
  background: #10b981;
  box-shadow: 0 0 10px #10b981;
}

/* Page Content */
#page-content-wrapper {
  flex: 1;
  transition: all 0.3s ease;
  background: var(--light-red);
}

#page-content-wrapper.sidebar-toggled {
  margin-left: calc(var(--sidebar-width) * -1);
}

.page-content {
  padding: 2rem;
  min-height: calc(100vh - 70px);
}

/* Responsive Design */
@media (max-width: 768px) {
  .admin-sidebar {
    position: fixed;
    z-index: 1000;
    height: calc(100vh - 70px);
  }
  
  .navigation-section {
    display: none;
  }
  
  .user-info {
    display: none;
  }
  
  .page-content {
    padding: 1rem;
  }
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: var(--light-red);
}

::-webkit-scrollbar-thumb {
  background: var(--primary-red);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--dark-red);
}
</style>